// Everything the fiche shows is read off the establishment here, as plain functions, so the
// sections only lay things out. Each derivation states facts we hold - a date, a document, a
// count - and never infers one we don't.

import { IEstablishment, IEstablishmentSignal } from "../../../interfaces/establishment";

const DAY = 86400000;

export const daysSince = (d: Date) => Math.floor((Date.now() - d.getTime()) / DAY);

export const fmtDate = (d?: string | Date | null, month: "short" | "long" = "short") => {
    if (!d) return null;
    const date = typeof d === "string" ? new Date(d) : d;
    if (isNaN(date.getTime())) return null;
    return date.toLocaleDateString("fr-FR", { day: "numeric", month, year: "numeric" });
};

export function relativeFr(d: Date): string {
    const days = daysSince(d);
    if (days < 0) {
        const ahead = -days;
        return ahead === 0 ? "aujourd'hui" : ahead === 1 ? "demain" : `dans ${ahead} j`;
    }
    if (days === 0) return "aujourd'hui";
    if (days === 1) return "hier";
    if (days < 7) return `il y a ${days} j`;
    if (days < 30) return `il y a ${Math.floor(days / 7)} sem.`;
    if (days < 365) return `il y a ${Math.floor(days / 30)} mois`;
    const years = Math.floor(days / 365);
    return `il y a ${years} an${years > 1 ? "s" : ""}`;
}

export function displayName(name?: string): string {
    if (!name) return "Établissement";
    return name.includes("[ND]") ? "Établissement confidentiel" : name;
}

/* ─── Trading status ─────────────────────────────────────────────────────── */

export type Tone = "success" | "warning" | "danger" | "neutral" | "info";

export interface TradingStatus {
    tone: Tone;
    label: string;
    closed: boolean;
    /** One line saying what the status rests on, shown under a closure. */
    detail?: string;
}

/**
 * Open / closed as the rep needs it: the registry state first, then a recorded closing date,
 * then a BODACC radiation. The old header printed `registration.status` raw ("active",
 * "closed") in a green pill whatever it said.
 */
export function tradingStatus(e: IEstablishment): TradingStatus {
    const closingDate = e.operations?.closing_date || e.registration?.closing_date;
    const radiation = (e.signals || []).find((s) => s.type === "bodacc_announcement" && s.metadata?.isRadiation === true);

    if (e.registration?.status === "closed" || closingDate) {
        return {
            tone: "danger",
            label: "Fermé",
            closed: true,
            detail: closingDate ? `Fermeture enregistrée le ${fmtDate(closingDate, "long")}` : "Fermé au répertoire SIRENE",
        };
    }
    if (radiation) {
        return { tone: "danger", label: "Radié", closed: true, detail: `Radiation publiée au BODACC le ${fmtDate(radiation.date, "long")}` };
    }
    if (e.registration?.status === "in_liquidation") {
        return { tone: "warning", label: "En liquidation", closed: false };
    }
    const lastSignal = [...(e.signals || [])].filter((s) => s.date).sort((a, b) => +new Date(b.date!) - +new Date(a.date!))[0];
    if (lastSignal?.type === "restaurant_closing") {
        return { tone: "warning", label: "Fermeture signalée", closed: false, detail: `Signalée le ${fmtDate(lastSignal.date, "long")}` };
    }
    if (e.registration?.status === "active") return { tone: "success", label: "Ouvert", closed: false };
    return { tone: "neutral", label: "Statut inconnu", closed: false };
}

/* ─── People ─────────────────────────────────────────────────────────────── */

export interface Person {
    id: string;
    name: string;
    mandate: string;
    age: number | null;
    phone: string;
    email: string;
    linkedinUrl: string;
    raw: any;
}

/** The backend only sends approved enrichments (plus `done` birthdates), so both count. */
function enrichment(person: any, type: string) {
    return person?.enrichments?.find((e: any) => e.type === type && (e.status === "done" || e.status === "approved"));
}

/** "MM-YYYY", as the birthdate enrichment stores it. */
function ageFrom(data: unknown): number | null {
    if (typeof data !== "string") return null;
    const [m, y] = data.split("-").map((p) => parseInt(p, 10));
    if (!m || !y) return null;
    const now = new Date();
    return now.getFullYear() - y - (now.getMonth() + 1 < m ? 1 : 0);
}

/**
 * The mandate as the registry filed it. `qualite` is the real thing ("Président",
 * "Directeur général", "Gérant", "Associé unique").
 */
function mandateLabel(person: any): string {
    const q = (person?.qualite || "").trim();
    if (q) return q;
    return person?.role === "primary" ? "Dirigeant" : "Contact";
}

/** Présidents and gérants lead; directeurs généraux and contacts follow. */
function mandateRank(person: any): number {
    const q = (person?.qualite || "").toLowerCase();
    if (/pr[ée]sident|g[ée]rant|associ[ée]\s+unique|entrepreneur/.test(q)) return 0;
    if (/directeur\s+g[ée]n[ée]ral/.test(q)) return 1;
    return person?.role === "primary" ? 2 : 3;
}

export function people(e: IEstablishment): Person[] {
    return [...(e.peoples || [])]
        .filter(Boolean)
        .sort((a, b) => mandateRank(a) - mandateRank(b))
        .map((p: any, i) => {
            const email = enrichment(p, "email")?.data?.email || enrichment(p, "email_company")?.data?.email || "";
            const phoneData = enrichment(p, "phone")?.data;
            const linkedinData = enrichment(p, "linkedin")?.data;
            return {
                id: String(p._id || i),
                name: p.name || "",
                mandate: mandateLabel(p),
                age: ageFrom(enrichment(p, "birthdate")?.data),
                phone: phoneData?.phone || phoneData?.number || "",
                email,
                linkedinUrl: linkedinData?.url || linkedinData?.linkedinUrl || "",
                raw: p,
            };
        });
}

/* ─── Signals & history ──────────────────────────────────────────────────── */

export function isDepot(sig: IEstablishmentSignal): boolean {
    const fam = (sig.metadata?.announcementFamily || "").toLowerCase();
    return fam.includes("dépôt") || fam.includes("depot") || fam.includes("compte");
}

export type BodaccFamily = "radiation" | "vente" | "modification" | "creation" | "other";

export function bodaccFamily(sig: IEstablishmentSignal): BodaccFamily {
    if (sig.metadata?.isRadiation === true) return "radiation";
    const raw = (sig.metadata?.announcementFamily || "").toLowerCase();
    if (raw.includes("radiat")) return "radiation";
    if (raw.includes("vent") || raw.includes("cess")) return "vente";
    if (raw.includes("modif")) return "modification";
    if (raw.includes("immatric") || raw.includes("créat") || raw.includes("creat")) return "creation";
    return "other";
}

const HYGIENE_BAD = ["A améliorer", "A corriger de manière urgente"];

export type HistoryKind =
    | "creation" | "opening" | "closing" | "owner_change" | "franchise" | "expansion" | "multi_site"
    | "hygiene" | "bodacc_radiation" | "bodacc_vente" | "bodacc_modification" | "bodacc_creation" | "bodacc_other"
    | "news";

export type HistoryFilter = "all" | "legal" | "hygiene" | "activity" | "news";

export interface HistoryItem {
    id: string;
    kind: HistoryKind;
    filter: Exclude<HistoryFilter, "all">;
    date: Date;
    title: string;
    detail?: string;
    tone: Tone;
    signal?: IEstablishmentSignal;
    href?: string;
}

export interface NewsLink {
    _id: string;
    title: string;
    link: string;
    published: string;
    category?: string;
    source?: { name?: string };
}

const SIGNAL_TITLES: Partial<Record<string, { kind: HistoryKind; title: string; filter: HistoryItem["filter"]; tone: Tone }>> = {
    restaurant_opening: { kind: "opening", title: "Ouverture", filter: "activity", tone: "success" },
    restaurant_closing: { kind: "closing", title: "Fermeture", filter: "activity", tone: "danger" },
    restaurant_owner_change: { kind: "owner_change", title: "Changement d'exploitant", filter: "activity", tone: "warning" },
    franchise_signing: { kind: "franchise", title: "Signature de franchise", filter: "activity", tone: "neutral" },
    network_expansion: { kind: "expansion", title: "Expansion du réseau", filter: "activity", tone: "neutral" },
    multi_site: { kind: "multi_site", title: "Nouveau site du dirigeant", filter: "activity", tone: "neutral" },
};

const BODACC_TITLES: Record<BodaccFamily, { kind: HistoryKind; title: string; tone: Tone }> = {
    radiation: { kind: "bodacc_radiation", title: "Radiation", tone: "danger" },
    vente: { kind: "bodacc_vente", title: "Vente / cession", tone: "warning" },
    modification: { kind: "bodacc_modification", title: "Modification", tone: "neutral" },
    creation: { kind: "bodacc_creation", title: "Immatriculation", tone: "neutral" },
    other: { kind: "bodacc_other", title: "Annonce BODACC", tone: "neutral" },
};

/** Every dated thing we hold about the place, newest first. Accounts filings are left out. */
export function history(e: IEstablishment, news: NewsLink[]): HistoryItem[] {
    const items: HistoryItem[] = [];
    const creation = e.registration?.creation_date;
    if (creation) {
        items.push({ id: "creation", kind: "creation", filter: "legal", date: new Date(creation), title: "Création de l'établissement", detail: e.registration?.legal_form, tone: "neutral" });
    }
    if (e.operations?.opening_date && !(e.signals || []).some((s) => s.type === "restaurant_opening")) {
        items.push({ id: "opening", kind: "opening", filter: "activity", date: new Date(e.operations.opening_date), title: "Ouverture", tone: "success" });
    }

    (e.signals || []).forEach((sig, i) => {
        if (!sig.date) return;
        const id = String(sig._id || sig.metadata?.bodacc_id || `sig-${i}`);
        const date = new Date(sig.date);
        if (sig.type === "bodacc_announcement") {
            if (isDepot(sig)) return;
            const meta = BODACC_TITLES[bodaccFamily(sig)];
            const detail = sig.metadata?.modificationDescription || sig.metadata?.announcementType;
            items.push({ id, kind: meta.kind, filter: "legal", date, title: meta.title, detail: detail !== meta.title ? detail : undefined, tone: meta.tone, signal: sig });
        } else if (sig.type === "hygiene_control") {
            const label: string | undefined = sig.metadata?.score_label;
            items.push({
                id, kind: "hygiene", filter: "hygiene", date, title: "Contrôle sanitaire", detail: label,
                tone: label && HYGIENE_BAD.includes(label) ? (label.startsWith("A corriger") ? "danger" : "warning") : label === "Très satisfaisant" ? "success" : "neutral",
                signal: sig,
            });
        } else {
            const meta = SIGNAL_TITLES[sig.type] || { kind: "bodacc_other" as HistoryKind, title: sig.type, filter: "activity" as const, tone: "neutral" as Tone };
            items.push({ id, kind: meta.kind, filter: meta.filter, date, title: meta.title, detail: sig.metadata?.description, tone: meta.tone, signal: sig });
        }
    });

    news.forEach((n) => {
        items.push({ id: `news-${n._id}`, kind: "news", filter: "news", date: new Date(n.published), title: n.title, detail: n.source?.name, tone: "neutral", href: n.link });
    });

    return items.filter((i) => !isNaN(i.date.getTime())).sort((a, b) => b.date.getTime() - a.date.getTime());
}

/* ─── Pourquoi maintenant ────────────────────────────────────────────────── */

export interface WhyNowItem {
    id: string;
    date: Date;
    fact: string;
    /** What the fact is a reason to call about - a lead for the rep, not a claim about the place. */
    angle: string;
    tone: Tone;
    historyId?: string;
}

/**
 * Fresh, dated facts that make this the week to call, newest first, at most three. Windows
 * follow the weekly brief's (`facts.ts`): six months for most events, a year for a change of
 * owner. A closed place has none.
 */
export function whyNow(e: IEstablishment, items: HistoryItem[], anniversary: { years: number; date: Date; daysUntil: number } | null): WhyNowItem[] {
    const out: WhyNowItem[] = [];
    const within = (d: Date, days: number) => daysSince(d) <= days;

    for (const it of items) {
        const sixMonths = within(it.date, 183);
        switch (it.kind) {
            case "opening":
                if (sixMonths) out.push({ id: it.id, historyId: it.id, date: it.date, tone: "success", fact: it.date > new Date() ? `Ouverture prévue le ${fmtDate(it.date, "long")}` : `Ouvert le ${fmtDate(it.date, "long")}`, angle: "Équipe et fournisseurs en cours de choix" });
                break;
            case "owner_change":
            case "bodacc_vente":
                if (within(it.date, 365)) out.push({ id: it.id, historyId: it.id, date: it.date, tone: "warning", fact: it.kind === "bodacc_vente" ? `Fonds vendu, publié le ${fmtDate(it.date, "long")}` : `Changement d'exploitant le ${fmtDate(it.date, "long")}`, angle: "Nouveau décideur à rencontrer" });
                break;
            case "franchise":
            case "expansion":
                if (sixMonths) out.push({ id: it.id, historyId: it.id, date: it.date, tone: "neutral", fact: `${it.title} le ${fmtDate(it.date, "long")}`, angle: "Réseau qui se déploie : plusieurs sites à équiper" });
                break;
            case "multi_site":
                if (sixMonths) out.push({ id: it.id, historyId: it.id, date: it.date, tone: "neutral", fact: `Le dirigeant a ouvert un autre site le ${fmtDate(it.date, "long")}`, angle: "Un seul interlocuteur pour plusieurs sites" });
                break;
            case "hygiene":
                if (sixMonths && (it.tone === "warning" || it.tone === "danger")) out.push({ id: it.id, historyId: it.id, date: it.date, tone: it.tone, fact: `Contrôle sanitaire « ${it.detail} » le ${fmtDate(it.date, "long")}`, angle: "Mise en conformité à faire" });
                break;
            case "news":
                if (sixMonths) out.push({ id: it.id, historyId: it.id, date: it.date, tone: "neutral", fact: `Dans la presse : ${it.title}`, angle: "Accroche toute trouvée pour le premier message" });
                break;
            case "creation":
                if (sixMonths && !items.some((x) => x.kind === "opening")) out.push({ id: it.id, historyId: it.id, date: it.date, tone: "success", fact: `Société créée le ${fmtDate(it.date, "long")}`, angle: "Jeune affaire, choix encore ouverts" });
                break;
        }
    }

    const sorted = out.sort((a, b) => b.date.getTime() - a.date.getTime());
    if (anniversary) {
        sorted.unshift({
            id: "anniversary",
            date: anniversary.date,
            tone: "neutral",
            fact: `Fête ses ${anniversary.years} ans le ${anniversary.date.toLocaleDateString("fr-FR", { day: "numeric", month: "long" })}`,
            angle: "Prétexte d'appel naturel",
        });
    }
    return sorted.slice(0, 3);
}

/* ─── When to call ───────────────────────────────────────────────────────── */

const DAYS_FR: Record<string, string> = {
    monday: "lundi", tuesday: "mardi", wednesday: "mercredi", thursday: "jeudi",
    friday: "vendredi", saturday: "samedi", sunday: "dimanche",
};
export const DAY_ORDER = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
export const dayLabel = (day: string) => {
    const l = DAYS_FR[day.toLowerCase()] || day;
    return l.charAt(0).toUpperCase() + l.slice(1);
};

const toMinutes = (hhmm?: string) => {
    if (!hhmm) return null;
    const m = /^(\d{1,2})[:h](\d{2})/.exec(hhmm);
    return m ? parseInt(m[1], 10) * 60 + parseInt(m[2], 10) : null;
};

export interface CallWindow {
    window: string;
    /** Whether the window was read off the place's own hours or is the trade's default. */
    fromHours: boolean;
    closedDays: string[];
}

/**
 * When someone is there and not in the middle of a service. Lunch (11h30–14h30) and dinner
 * (18h30–22h) are off limits; within what is left, the window has to fall while the place is
 * open, which is read off its usual opening time.
 */
export function callWindow(e: IEstablishment): CallWindow {
    const hours = e.operations?.opening_hours || [];
    const closedDays = hours.filter((h) => h.closed || !h.open).map((h) => DAYS_FR[h.day.toLowerCase()] || h.day);
    const opens = hours.filter((h) => !h.closed && h.open).map((h) => toMinutes(h.open)).filter((m): m is number => m !== null);
    if (opens.length === 0) {
        return { window: "10h–11h30 ou 15h–17h30", fromHours: false, closedDays };
    }
    const sorted = [...opens].sort((a, b) => a - b);
    const usualOpen = sorted[Math.floor(sorted.length / 2)];
    let window: string;
    if (usualOpen <= 10 * 60) window = "10h–11h30 ou 15h–17h30";
    else if (usualOpen <= 15 * 60) window = "15h–17h30";
    else {
        const h = Math.floor(usualOpen / 60);
        window = `${h - 1}h–${h}h, avant l'ouverture`;
    }
    return { window, fromHours: true, closedDays };
}

/* ─── Revenue ────────────────────────────────────────────────────────────── */

export function revenue(e: IEstablishment) {
    const yearly = [...(e.revenue?.yearly || [])].sort((a, b) => a.year - b.year);
    if (yearly.length === 0) return null;
    const last = yearly[yearly.length - 1];
    const prev = yearly.length > 1 ? yearly[yearly.length - 2] : null;
    const delta = prev && prev.amount ? Math.round(((last.amount - prev.amount) / prev.amount) * 100) : null;
    return { year: last.year, amount: last.amount, delta, prevYear: prev?.year };
}

export const fmtEuros = (amount: number) =>
    amount >= 1_000_000
        ? `${(amount / 1_000_000).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} M€`
        : `${Math.round(amount / 1000).toLocaleString("fr-FR")} k€`;

/* ─── Misc ───────────────────────────────────────────────────────────────── */

export function addressLines(e: IEstablishment) {
    const line1 = e.address?.street || "";
    const line2 = [e.address?.postcode, e.address?.city].filter(Boolean).join(" ");
    return { line1, line2, full: [line1, line2].filter(Boolean).join(", ") };
}

export function mapsLinks(e: IEstablishment) {
    const geo = e.address?.geo?.coordinates;
    const hasGeo = !!geo && geo.length === 2;
    const { full } = addressLines(e);
    const query = encodeURIComponent([displayName(e.name), full].filter(Boolean).join(", "));
    return {
        place: e.ratings?.source_summary?.find((s) => s.source === "google")?.url
            ?? (hasGeo ? `https://www.google.com/maps?q=${geo![1]},${geo![0]}` : `https://www.google.com/maps/search/${query}`),
        directions: hasGeo
            ? `https://www.google.com/maps/dir/?api=1&destination=${geo![1]},${geo![0]}`
            : `https://www.google.com/maps/dir/?api=1&destination=${query}`,
    };
}

export const PROSPECT_STATUSES: Array<{ value: string; label: string }> = [
    { value: "new", label: "À contacter" },
    { value: "contacted", label: "Contacté" },
    { value: "responded", label: "En discussion" },
    { value: "qualified", label: "En négociation" },
    { value: "won", label: "Client" },
    { value: "lost", label: "Perdu" },
    { value: "inactive", label: "En pause" },
];
